import { useCallback, useRef } from 'react';

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const DRAFT_DEBOUNCE_MS = 1000;

export interface AnalisisDraft<T> {
  uidProtocolo: string;
  timestamp: number;
  data: T;
}

export function useAnalisisDraft<T>(prefix: string) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveDraft = useCallback(
    (uidProtocolo: string, data: unknown): void => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const draft: AnalisisDraft<unknown> = { uidProtocolo, timestamp: Date.now(), data };
        try {
          localStorage.setItem(`${prefix}-draft-${uidProtocolo}`, JSON.stringify(draft));
        } catch {
          // quota exceeded or storage unavailable
        }
      }, DRAFT_DEBOUNCE_MS);
    },
    [prefix],
  );

  const loadDraft = useCallback(
    (uidProtocolo: string): AnalisisDraft<T> | null => {
      try {
        const raw = localStorage.getItem(`${prefix}-draft-${uidProtocolo}`);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as AnalisisDraft<T>;
        if (Date.now() - parsed.timestamp > DRAFT_TTL_MS) {
          localStorage.removeItem(`${prefix}-draft-${uidProtocolo}`);
          return null;
        }
        return parsed;
      } catch {
        return null;
      }
    },
    [prefix],
  );

  const clearDraft = useCallback(
    (uidProtocolo: string): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      try {
        localStorage.removeItem(`${prefix}-draft-${uidProtocolo}`);
      } catch {
        // silently ignore
      }
    },
    [prefix],
  );

  return { saveDraft, loadDraft, clearDraft };
}
